import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ContextBase, ContextConfig, ContextParameters } from '../../../models/response/AtpConfig';
import { ContextAtpEnum } from '../../../../../common/enums/ContextAtpEnum';
import { FormArrayContextParameters, FormGroupContextBase, FormGroupContextConfig, FormGroupSubConfig } from '../models/FormModels';
import { EnabledModulesEnum } from '../../../../../common/enums/EnabledModulesEnum';
import { AtpSaveRequest } from '../../../models/request/AtpSaveRequest';
import { AtpModifyRequest } from '../../../models/request/AtpModifyRequest';

@Injectable({
  providedIn: 'root'
})
export class VoucherAtpHandlerService {
  constructor(private formBuilder: FormBuilder) {}

  createFormGroupContextConfig(contexts: ContextConfig, disabled: boolean): FormGroup<FormGroupContextConfig> {
    const formGroupHandler: { [key: string]: FormGroup<FormGroupContextBase> } = {};

    for (const formGroupKey in contexts) {
      if (contexts[formGroupKey]) {
        formGroupHandler[formGroupKey] = this.createFormGroupContextBase(formGroupKey, contexts[formGroupKey], disabled);
      }
    }

    return this.formBuilder.group(formGroupHandler);
  }

  createFormArrayContextConfig(contexts: ContextConfig, disabled: boolean): FormArray<FormGroup<FormGroupContextBase>> {
    const formArray = new FormArray<FormGroup<FormGroupContextBase>>([]);

    for (const key in contexts) {
      if (contexts[key]) {
        formArray.push(this.createFormGroupContextMain(key, contexts[key], disabled));
      }
    }

    return formArray;
  }

  createFormGroupContextMain(name: string, contextConfig: ContextBase, disabled: boolean): FormGroup<FormGroupContextBase> {
    return new FormGroup<FormGroupContextBase>({
      name: new FormControl<string>({ value: name, disabled }, Validators.required),
      configType: new FormControl(
        { value: 'MAIN', disabled: true },
        {
          nonNullable: true
        }
      ),
      enabledModules: new FormControl<EnabledModulesEnum[]>({
        value: contextConfig?.enabledModules,
        disabled
      }),
      parameters: this.createFormArrayParameters(contextConfig?.parameters ?? {}, disabled),
      secureParameters: this.createFormArrayParameters(contextConfig?.secureParameters ?? {}, disabled),
      subConfig: this.createFormArraySubConfig(contextConfig?.subConfig ?? undefined, disabled)
    });
  }

  createFormGroupContextBase(name: string, contextConfig: ContextBase, disabled: boolean): FormGroup<FormGroupContextBase> {
    return new FormGroup<FormGroupContextBase>({
      name: new FormControl<string>({ value: name, disabled }, Validators.required),
      configType: new FormControl(
        { value: contextConfig.configType, disabled },
        {
          nonNullable: true
        }
      ),
      enabledModules: new FormControl<EnabledModulesEnum[]>({
        value: contextConfig?.enabledModules,
        disabled
      }),
      parameters: this.createFormArrayParameters(contextConfig?.parameters ?? {}, disabled),
      secureParameters: this.createFormArrayParameters(contextConfig?.secureParameters ?? {}, disabled),
      subConfig: this.createFormArraySubConfig(contextConfig?.subConfig ?? undefined, disabled)
    });
  }

  private createFormArrayContextBase(
    contextConfig: ContextConfig | undefined,
    disabled: boolean
  ): FormArray<FormGroup<FormGroupContextBase>> {
    const formArray = new FormArray<FormGroup<FormGroupContextBase>>([]);

    for (const key in contextConfig) {
      const formGroup = this.createFormGroupContextBase(key, contextConfig[key], disabled);
      formArray.push(formGroup);
    }

    return formArray;
  }

  private createFormArraySubConfig(subConfig: ContextConfig | undefined, disabled: boolean): FormArray<FormGroup<FormGroupSubConfig>> {
    const formArray = new FormArray<FormGroup<FormGroupSubConfig>>([]);

    for (const key in subConfig) {
      const formGroup = new FormGroup<FormGroupSubConfig>({
        name: new FormControl<string>({ value: key, disabled }, Validators.required),
        configType: new FormControl<ContextAtpEnum>({
          value: subConfig[key].configType,
          disabled
        }),
        subConfig: this.createFormArrayContextBase(subConfig[key].subConfig, disabled)
      });
      formArray.push(formGroup);
    }

    return formArray;
  }

  private createFormArrayParameters(
    parameters: ContextParameters | undefined,
    disabled: boolean
  ): FormArray<FormGroup<FormArrayContextParameters>> {
    const formArray = new FormArray<FormGroup<FormArrayContextParameters>>([]);

    if (!parameters && !Object.keys(parameters).length) {
      return formArray;
    }

    for (const key in parameters) {
      const formGroup = this.createFormGroupParameters(key, parameters[key], disabled);
      formArray.push(formGroup);
    }

    return formArray;
  }

  createFormGroupParameters(name: string, value: string, disabled: boolean): FormGroup<FormArrayContextParameters> {
    return new FormGroup<FormArrayContextParameters>({
      name: new FormControl({ value: name ?? '', disabled }, Validators.required),
      value: new FormControl({ value: value ?? '', disabled }, Validators.required)
    });
  }

  getAtpSaveRequest(formArray: FormArray<FormGroup<FormGroupContextBase>>): AtpSaveRequest {
    return {
      contexts: this.getContextFromArray(formArray)
    };
  }

  private getContextFromArray(formArray: FormArray<FormGroup<FormGroupContextBase>>): ContextConfig {
    const contexts: ContextConfig = {};

    formArray.controls.forEach((formGroup) => {
      const contextName = formGroup.controls.name.value ?? 'InvalidContextName';
      contexts[contextName] = this.getContextBase(formGroup);
    });

    return contexts;
  }

  getAtpModifyRequest(formGroup: FormArray<FormGroup<FormGroupContextBase>>): AtpModifyRequest {
    return {
      configuration: this.getAtpSaveRequest(formGroup)
    };
  }

  private formGroupToContext(formGroup: FormGroup<FormGroupContextConfig>) {
    const contexts: ContextConfig = {};

    for (const formGroupKey in formGroup.controls) {
      const abstractControl: FormGroup<FormGroupContextBase> = formGroup.get(formGroupKey) as FormGroup<FormGroupContextBase>;
      if (abstractControl) {
        contexts[formGroupKey] = this.getContextBase(abstractControl);
      }
    }

    return contexts;
  }

  getContextConfig(formGroup: FormGroup<FormGroupContextConfig>): ContextConfig {
    const contexts: ContextConfig = {};

    for (const formGroupKey in formGroup.controls) {
      const abstractControl: FormGroup<FormGroupContextBase> = formGroup.get(formGroupKey) as FormGroup<FormGroupContextBase>;
      if (abstractControl) {
      }
    }

    return contexts;
  }

  private getContextBase(formGroup: FormGroup<FormGroupContextBase>): ContextBase {
    return {
      configType: formGroup.controls.configType.value,
      enabledModules: formGroup.controls.enabledModules.value ?? undefined,
      parameters: this.getContextParameters(formGroup.controls.parameters.value ?? []),
      secureParameters: this.getContextParameters(formGroup.controls.secureParameters.value ?? []),
      subConfig: formGroup.controls.subConfig ? this.getSubConfig(formGroup.controls.subConfig) : undefined
    };
  }

  private getContextParameters(
    formArray: Partial<{
      name: string;
      value: string;
    }>[]
  ): ContextParameters | undefined {
    if (!formArray.length) {
      return undefined;
    }

    const parameters: ContextParameters = {};

    formArray.forEach((item) => {
      if (item.name) {
        parameters[item.name] = item.value;
      } else {
        console.error('Invalid ContextParameters name', item.name);
      }
    });

    return parameters;
  }

  getSubConfig(formArray: FormArray<FormGroup<FormGroupSubConfig>>): ContextConfig {
    const subConfig: ContextConfig = {
      domains: {
        configType: 'DOMAIN_LIST',
        subConfig: {}
      }
    };

    formArray.controls.forEach((control) => {
      if (control.controls.name.value) {
        const subContexts: any = {};

        control.controls.subConfig.controls.forEach((subConfigControl) => {
          if (subConfigControl.controls.name.value) {
            subContexts[subConfigControl.controls.name.value] = this.getContextSubconfig(subConfigControl);
          } else {
            console.log('Invalid subContexts name', subConfigControl.controls.name);
          }
        });

        subConfig.domains.subConfig = subContexts;
      }
    });

    return subConfig;
  }

  private getContextSubconfig(formGroup: FormGroup<FormGroupContextBase>): ContextBase {
    return {
      configType: formGroup.controls.configType.value,
      parameters: this.getContextParameters(formGroup.controls.parameters.value ?? []),
      secureParameters: this.getContextParameters(formGroup.controls.secureParameters.value ?? [])
    };
  }
}
